import React, { useEffect, useRef, useState } from "react";
import { Modal, Container, Form } from "react-bootstrap";

import Image from "next/image";
import Link from "next/link";
import { SerchIcon } from "@/components/icons/navbar-icon";
import { popularsearchcontent } from "@/constants/searchbarpopup";
import { usePathname, useRouter } from "next/navigation";
import axiosInstance from "@/axiosInstance";
import { CloseIcon } from "../icons/common-button-icon";

export default function SearchBarPopup({ show, onHide }) {
  const [searchQuery, setSearchQuery] = useState("");
  const router = useRouter(); // Initialize useRouter
  const pathname = usePathname();
  const [quickResults, setQuickResults] = useState({
    categories: [],
    products: [],
  });
  const debounceRef = useRef(null);
  const inputRef = useRef(null);
  const [loadingResults, setLoadingResults] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  const handleSearch = async () => {
    if (searchQuery.trim()) {
      onHide();
      router.push(`/search?query=${searchQuery}`);
    }
  };

  const handleInputChange = (event) => {
    const value = event.target.value;
    setSearchQuery(value);

    // Clear results if empty
    if (value.trim().length === 0) {
      setQuickResults({ categories: [], products: [] });
      setHighlightedIndex(-1);
      return;
    }

    // Clear previous debounce
    if (debounceRef.current) clearTimeout(debounceRef.current);

    // Debounced API call
    debounceRef.current = setTimeout(async () => {
      if (value.trim().length < 1) {
        setQuickResults({ categories: [], products: [] });
        setHighlightedIndex(-1);
        return;
      }

      setLoadingResults(true);
      try {
        const res = await axiosInstance.get(
          `/api/website/products/quicksearch?key=${value}`
        );
        setQuickResults({
          categories: res?.data?.categories || [],
          products: res?.data?.products || [],
        });
      } catch (err) {
        console.error("Quick search error:", err);
        setQuickResults({ categories: [], products: [] });
      } finally {
        setLoadingResults(false);
      }
    }, 300); // waits 300ms after typing stops
  };

  const handleClose = () => {
    onHide();
    setSearchQuery("");
    setQuickResults({ categories: [], products: [] });
  };

  const getHighlightedItemName = (index) => {
    if (index < quickResults.categories.length) {
      return quickResults.categories[index]?.name || "";
    } else {
      const prodIndex = index - quickResults.categories.length;
      return quickResults.products[prodIndex]?.name || "";
    }
  };

  const handleKeyDown = (event) => {
    const totalItems = [...quickResults.categories, ...quickResults.products];

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setHighlightedIndex((prev) => {
        const next = (prev + 1) % totalItems.length;
        setSearchQuery(getHighlightedItemName(next)); // Update input
        return next;
      });
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setHighlightedIndex((prev) => {
        const next = prev <= 0 ? totalItems.length - 1 : prev - 1;
        setSearchQuery(getHighlightedItemName(next)); // Update input
        return next;
      });
    } else if (event.key === "Enter") {
      const selectedItem = totalItems[highlightedIndex];

      if (selectedItem) {
        onHide();
        setHighlightedIndex(-1);
        setSearchQuery("");
        if (highlightedIndex < quickResults.categories.length) {
          router.push(`/${selectedItem.slug}`);
        } else {
          router.push(`/product/${selectedItem.slug}`);
        }

        setTimeout(() => {
          setQuickResults({ categories: [], products: [] });
        }, 100);
      } else {
        handleSearch(); // fallback to full search if no item is highlighted
      }
    }
  };

  useEffect(() => {
    if (show && inputRef.current) {
      inputRef.current.focus();
    }
  }, [show]);

  useEffect(() => {
    if (show && pathname === "/") {
      setSearchQuery("");
      setQuickResults({ categories: [], products: [] });
      setHighlightedIndex(-1);
    }
  }, [pathname, show]);

  return (
    <Modal
      className="serachbar-popup"
      show={show}
      onHide={onHide}
      dialogClassName="m-0 border-0 searchbar-dialog"
      contentClassName="rounded-0 d-block p-0 border-0"
    >
      <div className="py-4">
        <Container>
          <div className="d-flex align-items-center justify-content-between searchbar-popup-header-content pb-4">
            <div className="flex-shrink-1 ">
              <Image
                src="/assets/images/navbar/logo/logo.svg"
                className="img-fluid position-static"
                fill
                alt="Site-Logo"
              />
            </div>

            {/* Desktop Search bar */}
            <div className="flex-grow-1 d-none d-lg-block">
              <div className=" position-relative">
                <Form.Control
                  ref={inputRef}
                  type="search"
                  placeholder="Search"
                  value={searchQuery}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyDown}
                  className="rounded-pill shadow-none fw-medium border-0"
                />
                <SerchIcon
                  className="cursor-default position-absolute top-50  translate-middle-y serch-ico"
                  onClick={handleSearch}
                />
              </div>
            </div>

            <div className="flex-shrink-1 text-end close-icon">
              <button
                className="p-0 border-0 bg-transparent close-btn"
                onClick={handleClose}
              >
                <CloseIcon size={12} />
              </button>
            </div>
          </div>

          <div className="searchbar-popluarcontent  px-sm-4 px-lg-0">
            {/* Tabs & Mobile Search bar */}
            <div className="d-lg-none mb-3">
              <div className=" position-relative">
                {/* 
                <Form.Control
                  type="search"
                  value={searchQuery}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyPress}
                  placeholder="Search"
                  className="rounded-pill shadow-none fw-normal border-0"
                /> */}
                <SerchIcon
                  className="cursor-default position-absolute top-50  translate-middle-y serch-ico"
                  onClick={handleSearch}
                />
              </div>
            </div>
            <div className="ms-1 ms-lg-0" style={{ marginTop: "-12px" }}>
              {loadingResults ? (
                <span className="text-muted">Searching...</span>
              ) : (
                <>
                  {/* Category Section */}
                  {quickResults.categories.length > 0 && (
                    <>
                      <p className="fw-semibold text-dark mb-1">Category:</p>
                      {quickResults.categories.map((cat, i) => (
                        <div
                          key={`cat-${i}`}
                          className="cursor-pointer py-1 px-2"
                          style={{
                            backgroundColor:
                              highlightedIndex === i
                                ? "#00968f"
                                : "transparent",
                            color: highlightedIndex === i ? "white" : "inherit",
                            borderRadius: "4px",
                          }}
                          onMouseEnter={() => setHighlightedIndex(i)}
                          onClick={() => {
                            onHide();
                            router.push(`/${cat.slug}`);
                            setSearchQuery("");
                            setHighlightedIndex(-1);
                            setTimeout(() => {
                              setQuickResults({ categories: [], products: [] });
                            }, 100);
                          }}
                        >
                          {cat.name}
                        </div>
                      ))}
                    </>
                  )}

                  {/* Product Section */}
                  {quickResults.products.length > 0 && (
                    <>
                      <p className="fw-semibold text-dark mt-3 mb-1">
                        Product:
                      </p>
                      {quickResults.products.map((prod, i) => {
                        const index = i + quickResults.categories.length;
                        return (
                          <div
                            key={`prod-${i}`}
                            className="cursor-pointer py-1 px-2"
                            style={{
                              backgroundColor:
                                highlightedIndex === index
                                  ? "#00968f"
                                  : "transparent",
                              color:
                                highlightedIndex === index
                                  ? "white"
                                  : "inherit",
                              borderRadius: "4px",
                            }}
                            onMouseEnter={() => setHighlightedIndex(index)}
                            onClick={() => {
                              onHide();
                              router.push(`/product/${prod.slug}`);
                              setSearchQuery("");
                              setHighlightedIndex(-1);
                              setTimeout(() => {
                                setQuickResults({
                                  categories: [],
                                  products: [],
                                });
                              }, 100);
                            }}
                          >
                            {prod.name}
                          </div>
                        );
                      })}
                    </>
                  )}

                  {/* No Results */}
                  {searchQuery.trim().length > 1 &&
                    quickResults.categories.length === 0 &&
                    quickResults.products.length === 0 && (
                      <span className="text-muted">No results found.</span>
                    )}
                </>
              )}
            </div>

            {/* <p className="fw-normal title ms-1 ms-lg-0">
                Popular Search of Kalindi
                </p>
                <div className="pt-2 mt-1 ms-1 ms-lg-0 d-flex flex-column popular-content">
                  {popularsearchcontent.map((item, i) => {
                    return (
                      <Link
                        key={i}
                        href={item.href}
                        className="text-decoration-none fw-noraml"
                      >
                        {item.name}
                      </Link>
                    );
                  })}
            </div> */}
          </div>
        </Container>
      </div>
    </Modal>
  );
}
